import { getBearerToken } from "../../../../api/postCall/bearerToken";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import {
  Hit,
  PostgresResponseForMedicalPaOrders,
} from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { ReviewPendingOrdersState } from "../hooks/useReviewPendingOrdersTableData";
import { generateReviewPendingOrdersTableDataFromAlgoliaRecords } from "../utils/generateTableDataFromAlgoliaRecords";

/**
 * Fetches the next page of review-pending orders from Postgres and appends
 * the results to the existing orders state and table data (infinite-scroll pattern).
 *
 * @param payload - Search parameters including the target page number
 * @param authStatusOptions - Available auth status options for badge rendering
 * @param setReviewPendingOrdersState - State setter for the orders loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 */
export const makePostCallForNextPagesDataFetch = async (
  payload: SearchForMedicalPaOrdersFromPostgresParams,
  authStatusOptions: AuthStatusOptionModel[],
  setReviewPendingOrdersState: React.Dispatch<
    React.SetStateAction<ReviewPendingOrdersState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
) => {
  setReviewPendingOrdersState((prev) => ({ ...prev, showinLineLoader: true }));
  try {
    await getBearerToken();
    const response = await searchForMedicalPaOrdersFromPostgres(payload);
    const data: PostgresResponseForMedicalPaOrders = JSON.parse(
      response,
    ) as PostgresResponseForMedicalPaOrders;
    setReviewPendingOrdersState((prev) => ({
      ...prev,
      data: {
        ...data,
        hits: appendHitsToExistingTableData(
          prev.data?.hits ?? [],
          data?.hits ?? [],
        ),
      },
    }));
    setTableData((prev) => [
      ...prev,
      ...generateReviewPendingOrdersTableDataFromAlgoliaRecords(
        data.hits ?? [],
        authStatusOptions,
      ),
    ]);
  } catch (error) {
    setReviewPendingOrdersState((prev) => ({
      ...prev,
      error: error as string,
    }));
  } finally {
    setReviewPendingOrdersState((prev) => ({
      ...prev,
      showinLineLoader: false,
    }));
  }
};

/**
 * Concatenates new hits onto the existing hits array.
 *
 * @param existingTableData - Previously fetched hit records
 * @param newHits - Newly fetched hit records to append
 * @returns Combined array of all hits
 */
const appendHitsToExistingTableData = (
  existingTableData: Hit[],
  newHits: Hit[],
) => {
  return existingTableData.concat(newHits);
};
